import _, { isEmpty } from 'lodash';
import flatten from 'lodash/flatten';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';

import { RegistrationSituationStatus, RenewalStatus } from '@appTypes/enums';
import { denSortOrder } from '@modules/events/constants';
import {
  ProgramId,
  firstClassRankOrder,
  memberTypeIds,
  objElementName,
  ranksOrder,
} from '@shared/constants';

import personNameBuilder from '../../utils/personNameBuilder';
import { canOptOut, findRegistrationRecord } from './utilsTyped';

export const normalizeRankData = rank => ({
  ...rank,
  ...(rank.rank ? {} : { rank: rank.name }),
  level: Number(rank.level || 0),
  rankOrder: Number(rank.rankOrder || 0),
  programId: Number(rank.programId || 0),
  unitTypeId: Number(rank.unitTypeId || 0),
});

export const adultPackRosterDtoToModel = roster =>
  (roster['users'] ?? []).map(member => ({
    userId: Number(member.userId),
    isAdult: true,
    isLeader: true,
    isParent: false,
    ...member,
    memberTypeId: memberTypeIds.adult,
    key3: member.key3 === 'True',
    personId: member.memberId,
    personShortFullName: personNameBuilder.short(member),
    age: member.age > 21 ? '(21+)' : '(18-21)',
    currentHighestRankApproved: { rank: '' },
    otherHighestRanksApproved: [],
    rosterOtherHighestRanksApproved: [],
  }));

const mergeHighestRanks = (...arrays) =>
  orderBy(
    uniqBy(orderBy(flatten(arrays), 'level').reverse(), 'unitTypeId'),
    'unitTypeId',
  );

const getRankData = (
  { highestRanksApproved = [], highestRanksAwarded = [] },
  currentUnitTypeId,
) => {
  const highestRanks = mergeHighestRanks(
    highestRanksApproved,
    highestRanksAwarded,
  ).map(rank => ({
    ...rank,
    isActiveUnit: rank.unitTypeId == currentUnitTypeId,
  }));
  const currentHighestRank = highestRanks.find(
    ({ unitTypeId }) => unitTypeId == currentUnitTypeId,
  );
  const boyScoutHighestRank = highestRanks.find(
    ({ unitTypeId }) => unitTypeId == ProgramId.BOY_SCOUT,
  );
  const cubScoutHighestRank = highestRanks.find(
    ({ unitTypeId }) => unitTypeId == ProgramId.CUB_SCOUT,
  );
  const rosterCurrentHighestRank = currentHighestRank ||
    boyScoutHighestRank ||
    cubScoutHighestRank || { programId: Infinity };
  const currentRankOrder = ranksOrder[(currentHighestRank || {}).rank] || 0;
  const rosterCurrentRankOrder = ranksOrder[rosterCurrentHighestRank.rank] || 0;
  const hasMinFirstClass =
    ranksOrder[(boyScoutHighestRank || {}).rank] >= firstClassRankOrder;

  return {
    hasMinFirstClass,
    currentHighestRankApproved: normalizeRankData({
      rankOrder: currentRankOrder,
      programId: currentUnitTypeId,
      unitTypeId: currentUnitTypeId,
      ...currentHighestRank,
    }),
    otherHighestRanksApproved: highestRanks
      .filter(({ unitTypeId }) => unitTypeId != currentUnitTypeId)
      .map(normalizeRankData),
    rosterCurrentHighestRankApproved: normalizeRankData({
      rankOrder: rosterCurrentRankOrder,
      ...rosterCurrentHighestRank,
    }),
    rosterOtherHighestRanksApproved: highestRanks
      .filter(({ id }) => id != rosterCurrentHighestRank.id)
      .map(normalizeRankData),
  };
};

export const youthPackRosterDtoToModel = (
  roster,
  currentUnitTypeId,
  unitParticipants = [],
) => {
  const members = roster['users'] ?? [];
  const allMembers = [...members, ...unitParticipants];

  return allMembers.map(member => {
    const age = member.age
      ? member.age
      : member.dateOfBirth && moment().diff(member.dateOfBirth, 'years');

    const memberTypeId =
      age < 18 ? memberTypeIds.youth : memberTypeIds.participant;
    const isUnitParticipant = memberTypeId === memberTypeIds.participant;

    return {
      userId: Number(member.userId),
      ...member,
      isAdult: false,
      isParent: false,
      isLeader: false,
      isUnitParticipant,
      memberTypeId,
      personShortFullName: personNameBuilder.short(member),
      personId: member.memberId,
      age,
      ...getRankData(member, currentUnitTypeId),
    };
  });
};

export const getPersonRenewalStatus = (
  personItem,
  renewalsInfo,
  hasValidPosition,
  isDenChief = false,
) => {
  const emptyStatus = {
    renewalStatus: RenewalStatus.NeedsToRegister,
    registrationExpireDt: '',
  };

  // SBL-6551: Den Chiefs are not retrieved via orgYouths endpoint, so they
  // won't be found in renewalsInfo. Mark them as active instead of NeedsToRegister
  const denChiefActiveStatus = {
    renewalStatus: RenewalStatus.Current,
    registrationExpireDt: '',
    registrationSituationStatus: RegistrationSituationStatus.Active,
  };

  if (isEmpty(renewalsInfo)) {
    return isDenChief ? denChiefActiveStatus : emptyStatus;
  }

  const pickData = record => {
    const baseProps = _.pick(record, [
      'renewalStatus',
      'isYearlyMembershipRenewalPaid',
      'bsaOrderId',
      'registrationExpireDt',
      'registrationId',
      'registrationSituationStatus',
    ]);

    return {
      ...baseProps,
      canOptOut: canOptOut(record) && hasValidPosition,
    };
  };

  const { isAdult, isLeader } = personItem;

  const fallbackStatus = isDenChief ? denChiefActiveStatus : emptyStatus;

  if (!isAdult && renewalsInfo.youthMembers.length) {
    // We can get multiple registrations. Get last with registrationEffectiveDt
    const foundPerson = findRegistrationRecord(
      renewalsInfo.youthMembers,
      personItem.personGuid,
    );
    return foundPerson ? pickData(foundPerson) : fallbackStatus;
  }

  if (isLeader && renewalsInfo.adultMembers.length) {
    // We can get multiple registrations. Get last with registrationEffectiveDt
    const foundPerson = findRegistrationRecord(
      renewalsInfo.adultMembers,
      personItem.personGuid,
    );
    return foundPerson ? pickData(foundPerson) : fallbackStatus;
  }

  return fallbackStatus;
};

export function sortSubUnitsBy(foundSubUnits, name) {
  if (foundSubUnits.some(sub => !!sub.denType)) {
    return _.sortBy(foundSubUnits, [
      den => (den[name] === objElementName.UNASSIGNED ? 1 : 0),
      den => _.indexOf(denSortOrder, den.denType?.toLowerCase()),
      den => den[name],
    ]);
  }

  return _.sortBy(foundSubUnits, [
    sub => (sub[name] === objElementName.UNASSIGNED ? 1 : 0),
    sub => sub[name].toLowerCase(),
  ]);
}

export const checkHasFilterActive = (filter, constants) =>
  Object.values(constants).some(value => filter[value]);
